import React from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";

import { ACCENT, Card, EmptyNote } from "./MeteredParts";
import { formatMoney, metricLabel, rangeDays } from "./meteredFormat";

const MAX_DATES_SHOWN = 31;

// "Oct 18, 2026, 6:42 AM EDT" — the viewer's own time zone with its abbreviation.
const formatWriteTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
};

const BUCKETS = [
  ["unmapped_users", "Unmapped users", "username present, person not matched"],
  ["unattributed", "Unattributed", "no username on the record"],
  ["unmapped_organizations", "Unmapped organizations", "organization not recognised"],
  ["unassigned_cost_centers", "Unassigned cost centers", "no cost center on the record"],
];

// How far the billing records can be trusted. Counts are billing documents, not people, and the four groups overlap.
// "Latest write" is not a sync time and a date without records isn't a confirmed ingestion failure, so nothing here
// is turned into a score or a completeness claim. Collapsed by default; the request is only made once it is opened.
const QualityPanel = ({ expanded, onToggle, data, isLoading, isError, onRetry, range, metric, rangeTooLong, maxDays }) => {
  const days = rangeDays(range);
  const without = data?.dates_without_records ?? [];
  const withRecords = data?.days_with_records?.length ?? Math.max(days - without.length, 0);

  return (
    <Card
      title="Data Coverage & Attribution"
      subtitle="How confidently can these numbers be attributed? Counts are billing records, not people."
      from={range.from}
      to={range.to}
      isInitialLoading={expanded && isLoading}
      actions={
        <Tooltip title={expanded ? "Hide data coverage" : "Show data coverage"} arrow>
          <IconButton size="small" aria-expanded={expanded} aria-label={expanded ? "Hide data coverage" : "Show data coverage"} onClick={onToggle} sx={{ border: "1px solid #e8e8e8", borderRadius: "8px" }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      }
    >
      {!expanded ? null : rangeTooLong ? (
        <EmptyNote>Data coverage can cover at most {maxDays} days. Choose a shorter period.</EmptyNote>
      ) : isError ? (
        <EmptyNote>
          Couldn't load data coverage.{" "}
          <Button size="small" onClick={onRetry} sx={{ textTransform: "none", color: ACCENT }}>
            Retry
          </Button>
        </EmptyNote>
      ) : data ? (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }} data-testid="quality-panel">
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Chip size="small" label={`Sync status: ${data.sync_status ?? "unknown"}`} sx={{ backgroundColor: "#eee" }} />
            <Chip size="small" label={`Coverage: ${data.coverage_status ?? "unverified"}`} sx={{ backgroundColor: "#eee" }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
              {data.record_count?.toLocaleString("en-US")} billing records in this period · {formatMoney(data.totals?.[metric])} {metricLabel(metric).toLowerCase()}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.78rem", color: "#777" }}>
            The groups below overlap, so don't add them up. The latest write isn't proof of a successful sync, and a date with no records may mean no usage or missing data — it isn't a confirmed failure.
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {BUCKETS.map(([key, label, note]) => (
              <Box key={key} data-testid={`quality-${key}`} sx={{ border: "1px solid #e8e8e8", borderRadius: "10px", p: 1.5 }}>
                <Typography sx={{ fontSize: "0.72rem", color: "#777" }}>{label}</Typography>
                <Typography sx={{ fontSize: "1.3rem", fontWeight: 700 }}>{(data[key]?.record_count ?? 0).toLocaleString("en-US")}</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#999" }}>
                  {note}
                  <br />
                  {formatMoney(data[key]?.[metric])} {metricLabel(metric).toLowerCase()}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
              Dates without records{" "}
              <span style={{ fontWeight: 400, color: "#999" }}>
                ({withRecords} of {days} days have records)
              </span>
            </Typography>
            {without.length === 0 ? (
              <Typography sx={{ fontSize: "0.78rem", color: "#999", mt: 0.5 }}>Every date in this period has at least one record.</Typography>
            ) : (
              <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 0.8 }}>
                {without.slice(0, MAX_DATES_SHOWN).map((date) => (
                  <Chip key={date} size="small" label={date} variant="outlined" sx={{ borderStyle: "dashed", fontSize: "0.72rem" }} />
                ))}
                {without.length > MAX_DATES_SHOWN && <Chip size="small" label={`+${without.length - MAX_DATES_SHOWN} more`} sx={{ fontSize: "0.72rem" }} />}
              </Box>
            )}
          </Box>

          <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
            Latest billing record written: {data.last_document_write ? formatWriteTime(data.last_document_write) : "no records"}
          </Typography>
        </Box>
      ) : null}
    </Card>
  );
};

export default QualityPanel;
